import { usePayoutOverviewAccess } from "../hooks/usePayoutOverviewAccess";
import { useGetList, useGetOne, useSidebarState } from "react-admin";
import { atom, useRecoilState } from "recoil";
import { recoilPersist } from "recoil-persist";
import EventIcon from "@mui/icons-material/Event";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EuroIcon from "@mui/icons-material/Euro";
import AdminIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PlaygroundIcon from "@mui/icons-material/SportsEsports";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Link,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { type MouseEventHandler, type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { InstructorIcon } from "../icons/InstructorIcon";
import { StudentsIcon } from "../icons/StudentsIcon";
import { ExamIcon } from "../icons/ExamIcon";
import { ReportIcon } from "../icons/ReportIcon";
import { DrivingSchoolIcon } from "../icons/DrivingSchoolIcon";
import { HomeIcon } from "../icons/HomeIcon";
import { TruckIcon } from "../icons/TruckIcon";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { currentUserRoles, grants } from "../backoffice.access_control";
import { useThemeColor } from "../hooks/useThemeColor";
import { Row } from "./Row";
import { type Student } from "../providers/studentsProvider";
import { Column } from "./Column";
import { useCollapse } from "../hooks/useCollapse";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { Badge2 } from "./Badge2";
import { getBkfNavigationItems } from "./bkfNavigation";

type SecondLevelMenuItem = [
  /* name: */ string,
  /* path: */ string,
  /* isActive: */ boolean,
  /* disabledReason: */ string?,
];
type FlyoutMenuId = "courses" | "students" | "exams" | "finance" | "drivingSchool" | "bkf" | "settings" | "playground";
type OpenFlyoutState = {
  menuId: FlyoutMenuId;
  anchorEl: HTMLElement;
};

const COLLAPSED_FLYOUT_CLOSE_DELAY_MS = 180;
const TOP_LEVEL_MENU_ITEM_HEIGHT_PX = 44;
const COLLAPSED_HOVER_BRIDGE_WIDTH_PX = 6;
const EXPANDED_HOVER_BRIDGE_WIDTH_PX = 4;
const FLYOUT_BRIDGE_TOP_OFFSET_PX = 36;

const { persistAtom } = recoilPersist();
export const sidebarPermanentStateAtom = atom<"expanded" | "collapsed" | "unset">({
  key: "sidebarPermanentState",
  default: "unset",
  effects: [persistAtom],
});

export function getSidebarVisibility(): {
  canSeeMyCalendar: boolean;
  canSeePersonal: boolean;
} {
  return {
    canSeeMyCalendar: currentUserRoles.includes("instructor"),
    canSeePersonal: grants.has("Employee:list") || grants.has("Instructor:list"),
  };
}

export function BackofficeSideBar(props: { variant?: "layout" | "mobileDrawer"; onNavigate?: () => void }) {
  const { variant, onNavigate } = props;
  const isMobileDrawer = variant === "mobileDrawer";
  const isMobileLayout = useMediaQuery("(max-width: 899px)");
  const isLargeScreen = useMediaQuery("(min-width: 1800px)");
  const [sidebarPermanentState, setSidebarPermanentState] = useRecoilState(sidebarPermanentStateAtom);
  const alwaysExpanded = isMobileDrawer || sidebarPermanentState === "expanded";
  const { isExpanded, isTriggered, expand, collapse } = useCollapse(() => {
    if (alwaysExpanded) {
      return false;
    }
    if (sidebarPermanentState === "collapsed") {
      return true;
    }
    return !isLargeScreen;
  });
  const showLabels = isMobileDrawer || isExpanded;
  useEffect(() => {
    if (isExpanded && !isMobileDrawer) {
      setSidebarPermanentState("expanded");
    }
  }, []);
  useEffect(() => {
    setSidebarOpen(true); // <-- Hack to prevent that tooltips are rendered in <MenuItemLink> elements
  }, []);
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: drivingSchool } = useGetOne<DrivingSchool>(
    "drivingSchools",
    {
      id: drivingSchoolId ?? "",
    },
    { enabled: !!drivingSchoolId },
  );
  const { pathname } = useLocation();
  const [_, setSidebarOpen] = useSidebarState();
  const [openFlyout, setOpenFlyout] = useState<OpenFlyoutState | null>(null);
  const [hoverFlyoutEnabled, setHoverFlyoutEnabled] = useState(false);
  const closeFlyoutTimeoutRef = useRef<number | null>(null);

  const cancelScheduledFlyoutClose = useCallback(() => {
    if (!closeFlyoutTimeoutRef.current) {
      return;
    }
    window.clearTimeout(closeFlyoutTimeoutRef.current);
    closeFlyoutTimeoutRef.current = null;
  }, []);
  const closeFlyoutKeepHoverMode = useCallback(() => {
    cancelScheduledFlyoutClose();
    setOpenFlyout(null);
  }, [cancelScheduledFlyoutClose]);
  const closeFlyoutAndDisableHoverMode = useCallback(() => {
    cancelScheduledFlyoutClose();
    setOpenFlyout(null);
    setHoverFlyoutEnabled(false);
  }, [cancelScheduledFlyoutClose]);
  const scheduleFlyoutClose = useCallback(() => {
    cancelScheduledFlyoutClose();
    closeFlyoutTimeoutRef.current = window.setTimeout(() => {
      setOpenFlyout(null);
      setHoverFlyoutEnabled(false);
      closeFlyoutTimeoutRef.current = null;
    }, COLLAPSED_FLYOUT_CLOSE_DELAY_MS);
  }, [cancelScheduledFlyoutClose]);
  const toggleFlyout = useCallback(
    (menuId: FlyoutMenuId, anchorEl: HTMLElement) => {
      cancelScheduledFlyoutClose();
      setOpenFlyout((previous) => {
        if (previous?.menuId === menuId) {
          setHoverFlyoutEnabled(false);
          return null;
        }
        setHoverFlyoutEnabled(true);
        return { menuId, anchorEl };
      });
    },
    [cancelScheduledFlyoutClose],
  );
  const openFlyoutOnHover = useCallback(
    (menuId: FlyoutMenuId, anchorEl: HTMLElement) => {
      cancelScheduledFlyoutClose();
      setOpenFlyout((previous) => {
        if (previous?.menuId === menuId) {
          return previous;
        }
        return { menuId, anchorEl };
      });
    },
    [cancelScheduledFlyoutClose],
  );
  const isFlyoutOpen = useCallback((menuId: FlyoutMenuId) => openFlyout?.menuId === menuId, [openFlyout]);
  const getFlyoutAnchorEl = useCallback(
    (menuId: FlyoutMenuId) => (openFlyout?.menuId === menuId ? openFlyout.anchorEl : null),
    [openFlyout],
  );
  useEffect(() => {
    closeFlyoutAndDisableHoverMode();
  }, [pathname, closeFlyoutAndDisableHoverMode]);
  useEffect(() => {
    return () => cancelScheduledFlyoutClose();
  }, [cancelScheduledFlyoutClose]);
  const isDashboard = pathname === "/" || /^\/drivingSchools\/([^/]+)$/.test(pathname);
  const isMyCalendar = pathname === "/my-calendar";
  const isCourses = pathname.includes("/courses");
  const isEmployees = pathname.includes("/employees") || pathname.startsWith("/instructors/");
  const { canSeeMyCalendar, canSeePersonal } = getSidebarVisibility();
  const { total: numberOfStudentsWithoutAnInstructor = 0 } = useGetList<Student>(
    "students",
    {
      filter: {
        drivingSchoolId,
        archived: false,
        status: "active",
        instructorIds: (val: Array<string>) => val.length === 0,
      },
    },
    { enabled: !!drivingSchoolId },
  );
  const isStudents = pathname.includes("/students") && !isEmployees;
  const studentsBadge = numberOfStudentsWithoutAnInstructor > 0 && (
    <Badge2 badgeContent={numberOfStudentsWithoutAnInstructor} className="Warten-auf-Zuweisung-Badge" />
  );
  const examsItems = useExamsItems(drivingSchoolId, pathname);
  const financeItems = useFinanceItems(drivingSchoolId, drivingSchool, pathname);
  const isReports = pathname.includes("/bi");
  const drivingSchoolItems = useDrivingSchoolItems(drivingSchoolId, isEmployees, pathname);
  const bkfItems = useBkfItems(drivingSchoolId, drivingSchool, pathname);
  const settingsItems = useSettingsItems(drivingSchoolId, drivingSchool, pathname);
  const isAdmin = !!pathname.match("/drivingSchools/[^/]+/admin");
  const playgroundItems = usePlaygroundItems(pathname);
  const isCalendarEventDispatcher = pathname.startsWith("/calendar-event/");

  if (!drivingSchoolId || isCalendarEventDispatcher) {
    return null;
  }

  if (!isMobileDrawer && isMobileLayout) {
    return null;
  }

  return (
    <div
      id="sidebar"
      style={{
        width: alwaysExpanded ? 260 : 60,
        position: "relative",
        marginRight: isMobileDrawer ? 0 : "20px",
        marginTop: isMobileDrawer ? 0 : "-28px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isExpanded ? "normal" : "flex-start",
          position: alwaysExpanded ? "initial" : "absolute",
          top: 0,
          left: 0,
          paddingLeft: isMobileDrawer ? "20px" : 0,
          paddingRight: alwaysExpanded ? 0 : "20px",
          paddingBottom: isMobileDrawer ? "20px" : 0,
          width: isMobileDrawer ? 240 : isTriggered ? 260 : 60,
          minHeight: isMobileDrawer ? "auto" : "calc(100vh - 88px)",
          transition: "width 0.25s ease-in-out",
          boxShadow: isExpanded && !alwaysExpanded ? "20px 0px 40px -10px var(--backoffice-shadow)" : "none",
          backgroundColor: "background.default",
          zIndex: alwaysExpanded ? "auto" : 10, // ensure it’s above the content
        }}
      >
        <Box sx={{ height: "30px" }} />
        <Column>
          <TopLevelSideBarLink
            icon={<HomeIcon />}
            label="Dashboard"
            to="/"
            onNavigate={onNavigate}
            isActive={isDashboard}
            showLabel={showLabels}
            onMouseEnter={closeFlyoutKeepHoverMode}
          />
          <TopLevelSideBarLink
            icon={<SchoolOutlinedIcon />}
            label="Kurse"
            to={`/drivingSchools/${drivingSchoolId}/courses`}
            onNavigate={onNavigate}
            isActive={isCourses}
            showLabel={showLabels}
            onMouseEnter={closeFlyoutKeepHoverMode}
          />
          {canSeeMyCalendar && (
            <TopLevelSideBarLink
              icon={<EventIcon />}
              label="Mein Kalender"
              to="/my-calendar"
              onNavigate={onNavigate}
              isActive={isMyCalendar}
              showLabel={showLabels}
              onMouseEnter={closeFlyoutKeepHoverMode}
            />
          )}
          {canSeePersonal && (
            <TopLevelSideBarLink
              icon={<InstructorIcon />}
              label="Personal"
              to={`/drivingSchools/${drivingSchoolId}/employees#instructors`}
              onNavigate={onNavigate}
              isActive={isEmployees}
              showLabel={showLabels}
              onMouseEnter={closeFlyoutKeepHoverMode}
            />
          )}
          <TopLevelSideBarLink
            icon={<StudentsIcon />}
            label="Fahrschüler"
            to={`/drivingSchools/${drivingSchoolId}/students`}
            onNavigate={onNavigate}
            isActive={isStudents}
            showLabel={showLabels}
            trailing={<Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>{studentsBadge}</Box>}
            onMouseEnter={closeFlyoutKeepHoverMode}
          />
          {grants.has("Exam:list") && (
            <TopLevelSideBarMenu
              icon={<ExamIcon />}
              menuId="exams"
              label="Prüfungen"
              menuItems={examsItems}
              isMobileDrawer={isMobileDrawer}
              showLabels={showLabels}
              isFlyoutOpen={isFlyoutOpen("exams")}
              isAnyFlyoutOpen={!!openFlyout}
              isHoverFlyoutEnabled={hoverFlyoutEnabled}
              flyoutAnchorEl={getFlyoutAnchorEl("exams")}
              onToggleFlyout={toggleFlyout}
              onHoverOpenFlyout={openFlyoutOnHover}
              onCloseFlyout={closeFlyoutAndDisableHoverMode}
              onScheduleFlyoutClose={scheduleFlyoutClose}
              onCancelScheduledFlyoutClose={cancelScheduledFlyoutClose}
              onNavigate={onNavigate}
            />
          )}
          {financeItems.length > 0 && (
            <TopLevelSideBarMenu
              icon={<EuroIcon />}
              menuId="finance"
              label="Finanzen"
              menuItems={financeItems}
              isMobileDrawer={isMobileDrawer}
              showLabels={showLabels}
              isFlyoutOpen={isFlyoutOpen("finance")}
              isAnyFlyoutOpen={!!openFlyout}
              isHoverFlyoutEnabled={hoverFlyoutEnabled}
              flyoutAnchorEl={getFlyoutAnchorEl("finance")}
              onToggleFlyout={toggleFlyout}
              onHoverOpenFlyout={openFlyoutOnHover}
              onCloseFlyout={closeFlyoutAndDisableHoverMode}
              onScheduleFlyoutClose={scheduleFlyoutClose}
              onCancelScheduledFlyoutClose={cancelScheduledFlyoutClose}
              onNavigate={onNavigate}
            />
          )}
          {(grants.has("viewAutovioBusinessIntelligenceData") ||
            grants.has("viewDrivingSchoolBusinessIntelligenceData")) && (
            <TopLevelSideBarLink
              icon={<ReportIcon />}
              label="Reports"
              to={`/drivingSchools/${drivingSchoolId}/bi`}
              onNavigate={onNavigate}
              isActive={isReports}
              showLabel={showLabels}
              onMouseEnter={closeFlyoutKeepHoverMode}
            />
          )}
          {drivingSchoolItems.length > 0 && (
            <TopLevelSideBarMenu
              icon={<DrivingSchoolIcon />}
              menuId="drivingSchool"
              label="Fahrschule"
              menuItems={drivingSchoolItems}
              isMobileDrawer={isMobileDrawer}
              showLabels={showLabels}
              isFlyoutOpen={isFlyoutOpen("drivingSchool")}
              isAnyFlyoutOpen={!!openFlyout}
              isHoverFlyoutEnabled={hoverFlyoutEnabled}
              flyoutAnchorEl={getFlyoutAnchorEl("drivingSchool")}
              onToggleFlyout={toggleFlyout}
              onHoverOpenFlyout={openFlyoutOnHover}
              onCloseFlyout={closeFlyoutAndDisableHoverMode}
              onScheduleFlyoutClose={scheduleFlyoutClose}
              onCancelScheduledFlyoutClose={cancelScheduledFlyoutClose}
              onNavigate={onNavigate}
            />
          )}
          <TopLevelSideBarMenu
            icon={<TruckIcon />}
            menuId="bkf"
            label="Bildungsträger"
            menuItems={bkfItems}
            isMobileDrawer={isMobileDrawer}
            showLabels={showLabels}
            isFlyoutOpen={isFlyoutOpen("bkf")}
            isAnyFlyoutOpen={!!openFlyout}
            isHoverFlyoutEnabled={hoverFlyoutEnabled}
            flyoutAnchorEl={getFlyoutAnchorEl("bkf")}
            onToggleFlyout={toggleFlyout}
            onHoverOpenFlyout={openFlyoutOnHover}
            onCloseFlyout={closeFlyoutAndDisableHoverMode}
            onScheduleFlyoutClose={scheduleFlyoutClose}
            onCancelScheduledFlyoutClose={cancelScheduledFlyoutClose}
            onNavigate={onNavigate}
          />
          {settingsItems.length > 0 && (
            <TopLevelSideBarMenu
              icon={<SettingsOutlinedIcon />}
              menuId="settings"
              label="Einstellungen"
              menuItems={settingsItems}
              isMobileDrawer={isMobileDrawer}
              showLabels={showLabels}
              isFlyoutOpen={isFlyoutOpen("settings")}
              isAnyFlyoutOpen={!!openFlyout}
              isHoverFlyoutEnabled={hoverFlyoutEnabled}
              flyoutAnchorEl={getFlyoutAnchorEl("settings")}
              onToggleFlyout={toggleFlyout}
              onHoverOpenFlyout={openFlyoutOnHover}
              onCloseFlyout={closeFlyoutAndDisableHoverMode}
              onScheduleFlyoutClose={scheduleFlyoutClose}
              onCancelScheduledFlyoutClose={cancelScheduledFlyoutClose}
              onNavigate={onNavigate}
            />
          )}
          {grants.has("admin") && (
            <TopLevelSideBarLink
              icon={<AdminIcon />}
              label="Admin"
              to={`/drivingSchools/${drivingSchoolId}/admin`}
              onNavigate={onNavigate}
              isActive={isAdmin}
              showLabel={showLabels}
              onMouseEnter={closeFlyoutKeepHoverMode}
            />
          )}
          {location.hostname !== "backoffice.autovio.de" && !location.search.includes("visualTest") && (
            <TopLevelSideBarMenu
              icon={<PlaygroundIcon />}
              menuId="playground"
              label="Playground"
              menuItems={playgroundItems}
              isMobileDrawer={isMobileDrawer}
              showLabels={showLabels}
              isFlyoutOpen={isFlyoutOpen("playground")}
              isAnyFlyoutOpen={!!openFlyout}
              isHoverFlyoutEnabled={hoverFlyoutEnabled}
              flyoutAnchorEl={getFlyoutAnchorEl("playground")}
              onToggleFlyout={toggleFlyout}
              onHoverOpenFlyout={openFlyoutOnHover}
              onCloseFlyout={closeFlyoutAndDisableHoverMode}
              onScheduleFlyoutClose={scheduleFlyoutClose}
              onCancelScheduledFlyoutClose={cancelScheduledFlyoutClose}
              onNavigate={onNavigate}
            />
          )}
        </Column>
        {!isMobileDrawer && (
          <Row
            sx={{
              mt: "auto",
              mb: "20px",
            }}
          >
            <IconButton
              sx={{
                backgroundColor: "action.hover",
                "&:hover": { backgroundColor: "action.selected" },
                transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
                transition: "transform 0.3s ease",
              }}
              onClick={() => {
                if (isExpanded) {
                  collapse();
                } else {
                  expand();
                }
                setSidebarPermanentState(isExpanded ? "collapsed" : "expanded");
              }}
              aria-label="Toggle sidebar"
            >
              <ExpandLessIcon />
            </IconButton>
          </Row>
        )}
      </Box>
    </div>
  );
}

type TopLevelSideBarLinkProps = {
  sx?: SxProps<Theme>;
  icon: JSX.Element;
  label: string;
  trailing?: JSX.Element;
  isActive?: boolean;
  isHovered?: boolean;
  showLabel?: boolean;
  showCollapsedTooltip?: boolean;
  onMouseEnter?: MouseEventHandler<HTMLElement>;
} & (
  | {
      to: string;
      onNavigate?: () => void;
      onClick?: never;
    }
  | {
      to?: never;
      onNavigate?: never;
      onClick: MouseEventHandler<HTMLElement>;
    }
);

function TopLevelSideBarLink(props: TopLevelSideBarLinkProps) {
  const navigate = useNavigate();
  const onClick =
    props.onClick ??
    (() => {
      navigate(props.to);
      props.onNavigate?.();
    });
  const primaryColor = useThemeColor("primary");
  const shouldShowCollapsedTooltip = props.showCollapsedTooltip !== false && !props.showLabel;
  const link = (
    <Link
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: 12,
      }}
      sx={
        [
          {
            "&": {
              p: "10px",
              gap: 1,
              borderRadius: 8,
              fontWeight: 500, // <-- Poppins Medium
              fontSize: "14px",
              ...(props.isActive
                ? { color: primaryColor.contrastText, backgroundColor: primaryColor.main }
                : props.isHovered
                  ? { color: "text.secondary", backgroundColor: "action.hover" }
                  : { color: "text.secondary" }),
              ...props.sx,
            },
          },
          {
            "&:hover": { textDecoration: "none" },
          },
          {
            "&:hover": props.isActive ? {} : { backgroundColor: "action.hover" },
          },
          {
            "&:active": props.isActive ? {} : { backgroundColor: "action.hover" },
          },
        ] as any
      }
      onClick={onClick}
      onMouseEnter={props.onMouseEnter}
    >
      <Row sx={{ alignItems: "center" }}>
        <div style={{ marginRight: props.showLabel ? "14px" : 0, height: "24px" }}>{props.icon}</div>
        {props.showLabel && <div>{props.label}</div>}
      </Row>
      {props.showLabel && (props.trailing ?? <div />)}
    </Link>
  );

  if (!shouldShowCollapsedTooltip) {
    return link;
  }

  return (
    <Tooltip title={props.label} placement="right" arrow disableInteractive enterDelay={120}>
      {link}
    </Tooltip>
  );
}

function TopLevelSideBarMenu(props: {
  icon: ReactElement;
  menuId: FlyoutMenuId;
  label: string;
  menuItems: Array<SecondLevelMenuItem>;
  isMobileDrawer: boolean;
  showLabels?: boolean;
  badge?: React.ReactNode;
  isFlyoutOpen: boolean;
  isAnyFlyoutOpen: boolean;
  isHoverFlyoutEnabled: boolean;
  flyoutAnchorEl: HTMLElement | null;
  onToggleFlyout: (menuId: FlyoutMenuId, anchorEl: HTMLElement) => void;
  onHoverOpenFlyout: (menuId: FlyoutMenuId, anchorEl: HTMLElement) => void;
  onCloseFlyout: () => void;
  onScheduleFlyoutClose: () => void;
  onCancelScheduledFlyoutClose: () => void;
  onNavigate?: () => void;
}) {
  const { badge } = props;
  const isCollapsedNavigation = !props.showLabels;
  const isActive = props.menuItems.some((it) => it[2]);
  const navigate = useNavigate();
  const open = props.isFlyoutOpen;
  const hoverBridgeWidth = isCollapsedNavigation ? COLLAPSED_HOVER_BRIDGE_WIDTH_PX : EXPANDED_HOVER_BRIDGE_WIDTH_PX;
  const flyoutOffset: [number, number] = props.isMobileDrawer ? [0, -76] : [isCollapsedNavigation ? -36 : 0, 4];

  const closeFlyoutWithDelay = useCallback(() => {
    if (!isCollapsedNavigation) {
      return;
    }
    props.onScheduleFlyoutClose();
  }, [isCollapsedNavigation, props]);

  const toggleMenu: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      props.onCancelScheduledFlyoutClose();

      // On desktop, clicking a hover-opened flyout trigger should keep the flyout open.
      if (!props.isMobileDrawer && open) {
        return;
      }

      props.onToggleFlyout(props.menuId, event.currentTarget);
    },
    [open, props],
  );
  const onMouseEnter: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      props.onCancelScheduledFlyoutClose();
      if (props.isMobileDrawer && !props.isAnyFlyoutOpen && !props.isHoverFlyoutEnabled) {
        return;
      }
      props.onHoverOpenFlyout(props.menuId, event.currentTarget);
    },
    [props],
  );
  const primaryColor = useThemeColor("primary");
  return (
    <Box
      onMouseLeave={closeFlyoutWithDelay}
      sx={{
        width: "100%",
        "&:hover .submenu-chevron": {
          opacity: 1,
        },
        "&:active .submenu-chevron": {
          opacity: 1,
        },
      }}
    >
      <TopLevelSideBarLink
        icon={props.icon}
        label={props.label}
        trailing={
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            {badge}
            <Box
              className="submenu-chevron"
              sx={{
                height: "24px",
                ml: "auto",
                display: "flex",
                alignItems: "center",
                opacity: open ? 1 : 0,
                transition: "opacity 120ms ease-in-out",
              }}
            >
              <NavigateNextIcon sx={{ color: isActive ? primaryColor.contrastText : "text.secondary" }} />
            </Box>
          </Box>
        }
        isActive={isActive}
        onClick={toggleMenu}
        onMouseEnter={onMouseEnter}
        isHovered={open}
        showLabel={props.showLabels}
        showCollapsedTooltip={false}
      />
      {
        <Popper
          open={open}
          anchorEl={props.flyoutAnchorEl}
          placement="right-start"
          modifiers={[
            {
              name: "offset",
              options: {
                offset: flyoutOffset,
              },
            },
          ]}
          sx={{ zIndex: 1400 }}
        >
          <ClickAwayListener onClickAway={props.onCloseFlyout}>
            <Box
              onMouseEnter={props.onCancelScheduledFlyoutClose}
              onMouseLeave={closeFlyoutWithDelay}
              sx={{
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: `${FLYOUT_BRIDGE_TOP_OFFSET_PX}px`,
                  height: `${TOP_LEVEL_MENU_ITEM_HEIGHT_PX}px`,
                  width: `${hoverBridgeWidth}px`,
                  transform: "translateX(-100%)",
                  backgroundColor: "transparent",
                }}
              />
              <Paper
                sx={{
                  minWidth: 220,
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              >
                <MenuList autoFocusItem={false} sx={{ p: 1 }}>
                  {isCollapsedNavigation && (
                    <Box sx={{ px: "12px", pt: "4px", pb: "8px" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {props.label}
                      </Typography>
                    </Box>
                  )}
                  {props.menuItems.map(([name, to, isMenuItemActive, disabledReason]) => (
                    <Tooltip key={name} title={disabledReason ?? ""} placement="right" disableInteractive>
                      <MenuItem
                        aria-disabled={!!disabledReason}
                        onClick={() => {
                          if (disabledReason) {
                            return;
                          }
                          props.onCancelScheduledFlyoutClose();
                          navigate(to);
                          props.onCloseFlyout();
                          props.onNavigate?.();
                        }}
                        sx={{
                          p: "10px 12px",
                          borderRadius: 1,
                          color: disabledReason ? "text.disabled" : "text.primary",
                          cursor: disabledReason ? "not-allowed" : "pointer",
                          backgroundColor: isMenuItemActive ? primaryColor.ultraLight : "transparent",
                          fontSize: "14px",
                          fontWeight: "normal",
                          "&:hover": {
                            backgroundColor: disabledReason
                              ? "transparent"
                              : isMenuItemActive
                                ? primaryColor.ultraLight
                                : "action.hover",
                          },
                        }}
                      >
                        <Typography variant="body2" fontWeight="normal">
                          {name}
                        </Typography>
                      </MenuItem>
                    </Tooltip>
                  ))}
                </MenuList>
              </Paper>
            </Box>
          </ClickAwayListener>
        </Popper>
      }
    </Box>
  );
}

function useExamsItems(drivingSchoolId: undefined | string, pathname: string) {
  const isExamsCalendar = pathname.includes("/exams-calendar");
  const isExamAssignment = pathname.includes("/examinationAssignments");
  const isExpiringSoonExamAssignments = pathname.includes("/expiringSoonExaminationAssignments");
  const isExpiredExamAssignments = pathname.includes("/expiredExaminationAssignments");
  const isTheoryExams = pathname.includes("/theoryExams");
  const isPracticalExams = pathname.includes("/practicalExams");
  const examsItems = useMemo<Array<SecondLevelMenuItem>>(
    () => [
      ["Kalender", `/drivingSchools/${drivingSchoolId}/exams-calendar`, isExamsCalendar],
      ["Prüfaufträge", `/drivingSchools/${drivingSchoolId}/examinationAssignments`, isExamAssignment],
      [
        "Prüfaufträge vor Ablauf",
        `/drivingSchools/${drivingSchoolId}/expiringSoonExaminationAssignments`,
        isExpiringSoonExamAssignments,
      ],
      [
        "Prüfaufträge abgelaufen",
        `/drivingSchools/${drivingSchoolId}/expiredExaminationAssignments`,
        isExpiredExamAssignments,
      ],
      ["Theorieprüfungen", `/drivingSchools/${drivingSchoolId}/theoryExams`, isTheoryExams],
      ["Praktische Prüfungen", `/drivingSchools/${drivingSchoolId}/practicalExams`, isPracticalExams],
    ],
    [
      drivingSchoolId,
      isExamsCalendar,
      isExamAssignment,
      isExpiringSoonExamAssignments,
      isExpiredExamAssignments,
      isTheoryExams,
      isPracticalExams,
    ],
  );
  return examsItems;
}

function useFinanceItems(
  drivingSchoolId: undefined | string,
  drivingSchool: undefined | DrivingSchool,
  pathname: string,
) {
  const isServiceEntries = pathname.includes("/serviceEntries");
  const hasNextGenerationInvoicing = drivingSchool?.customizations?.includes("nextGenerationInvoicing");
  const isPerformanceOverview = pathname.includes("/performanceOverview");
  const isAdvancePayments = pathname.includes("/advancePayments");
  const isOpenInvoices = pathname.includes("/openInvoices");
  const isPayouts = pathname.includes("/payouts");
  const isTax = pathname.includes("/tax");
  const isDATEV = pathname.includes("/datev");
  const canSeeDATEV = grants.has("viewDATEV") || grants.has("editDATEVConfig");
  const {
    total: numberOfDATEVConfigs = 0,
    isLoading: isLoadingDATEVConfigs,
    error: datevConfigsError,
  } = useGetList(
    "datevConfigs",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "valid_from", order: "DESC" },
    },
    { enabled: !!drivingSchoolId && canSeeDATEV, keepPreviousData: false },
  );
  const hasDATEVConfig = canSeeDATEV && !isLoadingDATEVConfigs && !datevConfigsError && numberOfDATEVConfigs > 0;
  const isDunning = pathname.includes("/dunning");
  const hasDunning = drivingSchool?.customizations?.includes("dunning");
  const isManualRefunds = pathname.includes("/manualRefunds");
  const isManualPayments = pathname.includes("/manualPayments");
  const isInvoiceCustomers = pathname.includes("/invoiceCustomers");
  const isPlatformPayments = pathname.includes("/platformPayments");
  const { canView: canViewPayoutOverview } = usePayoutOverviewAccess(drivingSchool);
  const hasUseStripeForB2BPayments = drivingSchool?.customizations?.includes("useUseStripeForB2BPayments");
  const financeItems = useMemo<Array<SecondLevelMenuItem>>(() => {
    let result: Array<SecondLevelMenuItem> = [];
    if (grants.has("viewPerformanceOverview")) {
      result = result.concat([
        ["Leistungsübersicht", `/drivingSchools/${drivingSchoolId}/performanceOverview`, isPerformanceOverview],
        ["Vorauszahlungen", `/drivingSchools/${drivingSchoolId}/advancePayments`, isAdvancePayments],
        ["Offene Posten", `/drivingSchools/${drivingSchoolId}/openInvoices`, isOpenInvoices],
        ["Manuelle Einzahlungen", `/drivingSchools/${drivingSchoolId}/manualPayments`, isManualPayments],
      ]);
    }
    if (grants.has("developer") || (hasNextGenerationInvoicing && grants.has("ServiceEntry:list"))) {
      result.push(["Leistungen", `/drivingSchools/${drivingSchoolId}/serviceEntries`, isServiceEntries]);
    }
    if (grants.has("viewDrivingSchoolInvoiceCustomers")) {
      result.push(["Rechnungskunden", `/drivingSchools/${drivingSchoolId}/invoiceCustomers`, isInvoiceCustomers]);
    }
    if (canViewPayoutOverview) {
      result.push(["Auszahlungen", `/drivingSchools/${drivingSchoolId}/payouts`, isPayouts]);
    }
    if (grants.has("viewPayoutInformation")) {
      result.push(["Steuerbüro-Exports", `/drivingSchools/${drivingSchoolId}/tax`, isTax]);
    }
    if (hasDATEVConfig) {
      result.push(["DATEV", `/drivingSchools/${drivingSchoolId}/datev`, isDATEV]);
    }
    if (hasDunning) {
      result.push(["Mahnwesen", `/drivingSchools/${drivingSchoolId}/dunning`, isDunning]);
    }
    if (grants.has("viewManualRefunds")) {
      result.push(["Manuelle Erstattungen", `/drivingSchools/${drivingSchoolId}/manualRefunds`, isManualRefunds]);
    }
    if (hasUseStripeForB2BPayments) {
      result.push(["Platform Zahlungen", `/drivingSchools/${drivingSchoolId}/platformPayments`, isPlatformPayments]);
    }
    return result;
  }, [
    drivingSchoolId,
    isPerformanceOverview,
    isServiceEntries,
    hasNextGenerationInvoicing,
    isAdvancePayments,
    isOpenInvoices,
    isInvoiceCustomers,
    isPayouts,
    isTax,
    isDATEV,
    hasDATEVConfig,
    isDunning,
    hasDunning,
    isManualRefunds,
    isManualPayments,
    isPlatformPayments,
    hasUseStripeForB2BPayments,
    canViewPayoutOverview,
  ]);
  return financeItems;
}

function useDrivingSchoolItems(
  drivingSchoolId: string | undefined,
  isEmployees: undefined | boolean,
  pathname: string,
) {
  const isBranches = pathname.includes("/branches");
  const isFleet =
    pathname.includes("/fleet") ||
    (pathname.includes("/vehicles") && !isEmployees) ||
    ["/cars/", "/motorcycles/", "/trailers/", "/simulators/", "/trucks/", "/buses/", "/tractors/"].some((it) =>
      pathname.startsWith(it),
    );
  const isExamLocations = pathname.includes("/examLocations");
  const drivingSchoolItems = useMemo<Array<SecondLevelMenuItem>>(() => {
    const result: Array<SecondLevelMenuItem> = [];
    if (grants.has("Branch:list")) {
      result.push(["Filialen", `/drivingSchools/${drivingSchoolId}/branches`, isBranches, undefined]);
    }
    if (grants.has("Fleet:list")) {
      result.push(["Fuhrpark", `/drivingSchools/${drivingSchoolId}/fleet`, isFleet, undefined]);
    }
    if (grants.has("ExamLocation:list")) {
      result.push(["Prüfungsorte", `/drivingSchools/${drivingSchoolId}/examLocations`, isExamLocations, undefined]);
    }
    return result;
  }, [drivingSchoolId, isBranches, isFleet, isExamLocations]);
  return drivingSchoolItems;
}

function useBkfItems(drivingSchoolId: string | undefined, drivingSchool: DrivingSchool | undefined, pathname: string) {
  return useMemo<Array<SecondLevelMenuItem>>(() => {
    if (!drivingSchoolId) {
      return [];
    }
    return getBkfNavigationItems({
      drivingSchoolId,
      drivingSchool,
      pathname,
      hasGrant: (grant) => grants.has(grant),
    }).map(({ label, to, isActive, disabledReason }) => [label, to, isActive, disabledReason]);
  }, [drivingSchoolId, drivingSchool, pathname]);
}

function useSettingsItems(
  drivingSchoolId: string | undefined,
  drivingSchool: undefined | DrivingSchool,
  pathname: string,
) {
  const isLabels = pathname.includes("/labels");
  const isOtherEventCategories = pathname.includes("/otherEventCategories");
  const isServiceTypes = pathname.includes("/serviceTypes");
  const isCustomCards = pathname.includes("/customCards");
  const hasSubscriptionPackages = drivingSchool?.customizations?.includes("subscriptionPackages");
  const isSubscriptionPackages = pathname.includes("/subscriptionPackages");
  const settingsItems = useMemo<Array<SecondLevelMenuItem>>(() => {
    const result: Array<SecondLevelMenuItem> = [];
    if (grants.has("DrivingSchoolLabel:list")) {
      result.push(["Fahrschüler-Labels", `/drivingSchools/${drivingSchoolId}/labels`, isLabels, undefined]);
    }
    if (grants.has("OtherEvent:category:list")) {
      result.push([
        "Arbeitszeiten",
        `/drivingSchools/${drivingSchoolId}/otherEventCategories`,
        isOtherEventCategories,
        undefined,
      ]);
    }
    if (grants.has("ServiceType:list")) {
      result.push(["Rechnungstypen", `/drivingSchools/${drivingSchoolId}/serviceTypes`, isServiceTypes, undefined]);
    }
    if (grants.has("CustomCard:list")) {
      result.push(["App Cockpit Teaser", `/drivingSchools/${drivingSchoolId}/customCards`, isCustomCards, undefined]);
    }
    if (hasSubscriptionPackages && grants.has("SubscriptionPackage:list")) {
      result.push([
        "AUTOVIO Pro",
        `/drivingSchools/${drivingSchoolId}/subscriptionPackages`,
        isSubscriptionPackages,
        undefined,
      ]);
    }
    return result;
  }, [
    drivingSchoolId,
    isLabels,
    isOtherEventCategories,
    isServiceTypes,
    isCustomCards,
    hasSubscriptionPackages,
    isSubscriptionPackages,
  ]);
  return settingsItems;
}

function usePlaygroundItems(pathname: string) {
  const isFieldsPlayground = pathname === "/playground/fields";
  const isInputsPlayground = pathname === "/playground/inputs";
  const isTabsPlayground = pathname === "/playground/tabs" || pathname.startsWith("/playground/tabs/");
  const isTabs2Playground = pathname === "/playground/tabs2" || pathname.startsWith("/playground/tabs2/");
  const isToastsPlayground = pathname === "/playground/toasts";
  const isIconsPlayground = pathname === "/playground/icons";
  const playgroundItems: Array<SecondLevelMenuItem> = [
    ["Fields", "/playground/fields", isFieldsPlayground, undefined],
    ["Inputs", "/playground/inputs", isInputsPlayground, undefined],
    ["Tabs", "/playground/tabs", isTabsPlayground, undefined],
    ["Tabs2", "/playground/tabs2", isTabs2Playground, undefined],
    ["Toasts", "/playground/toasts", isToastsPlayground, undefined],
    ["Icons", "/playground/icons", isIconsPlayground, undefined],
  ];
  return playgroundItems;
}
